<template>
  <v-app
    id="inspire"
    :class="{ 'is-dark': darkMode }"
  >
    <div class="climbing-scene" aria-hidden="true">
      <div ref="gameRoute" class="game-route">
        <span
          v-for="(hold, index) in routeHolds"
          :key="index"
          class="route-hold"
          :class="{
            'is-reached': index <= currentHoldIndex,
            'is-current': index === currentHoldIndex,
            'is-top': hold.isTop,
          }"
          :style="{ bottom: `${hold.bottom}%`, left: `${hold.left}%` }"
        ></span>

        <!-- Climber Rig with Manga Avatar -->
        <div
          ref="climberRig"
          class="climber-rig"
          :class="{
            'is-catch': climberPose === 'catch',
            'is-reach': climberPose === 'reach',
            'is-glowing': climberGlow,
          }"
          :style="{
            '--climber-top': `${climberTop}%`,
            '--climber-left': `${climberLeft}%`,
            '--climber-x': `${climberLateral}px`,
            '--climber-lift': `${climberLift}px`,
            '--climber-tilt': `${climberTilt}deg`,
            '--climber-scale': climberScale,
          }"
        >
          <img
            src="./assets/climber-game-avatar-catch.png"
            alt=""
            class="game-climber game-climber--catch"
          >
          <img
            src="./assets/climber-game-avatar-manga.png"
            alt=""
            class="game-climber game-climber--reach"
          >
        </div>

        <!-- High performance chalk particle canvas -->
        <canvas ref="fxCanvas" class="route-fx-canvas"></canvas>
      </div>

      <div
        class="route-top"
        :class="{ 'is-reached': currentHoldIndex >= routeHolds.length - 1 || learningProgress >= 100 }"
      >
        <v-icon small color="inherit" class="mr-1">mdi-trophy</v-icon>
        TOP
      </div>
    </div>

    <v-container class="app-frame py-4 py-md-8 px-2 px-md-4">
      <v-row justify="center">
        <v-col cols="12" sm="12" md="10" lg="8">
          <v-card elevation="10" class="learning-shell rounded-xl overflow-hidden">
            <header class="brand-bar pa-4 pa-md-5 text-center">
              <div class="brand-overline">LANGUAGE ROUTE</div>
              <div class="d-flex align-center justify-center mt-1">
                <v-icon class="brand-mark mr-2" size="26">mdi-terrain</v-icon>
                <h1 class="brand-name mb-0">Langi</h1>
              </div>
              <p class="brand-tagline mb-0 mt-1">Wspinaj się słowo po słowie</p>
            </header>
            <div class="learning-content pa-3 pa-md-6">
              <HelloWorld
                @dark-mode-changed="darkMode = $event"
                @learning-progress="setLearningProgress"
                @answer-result="applyAnswerPhysics"
              />
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { gsap } from 'gsap';
import HelloWorld from "./components/HelloWorld.vue";

const ROUTE_HOLDS = [
  { bottom: 4, left: 48, label: '1' },
  { bottom: 18, left: 21, label: '2' },
  { bottom: 31, left: 67, label: '3' },
  { bottom: 44, left: 38, label: '4' },
  { bottom: 58, left: 72, label: '5' },
  { bottom: 73, left: 31, label: '6' },
  { bottom: 88, left: 56, label: '7' },
  { bottom: 97, left: 50, label: 'TOP', isTop: true },
];

export default {
  name: "App",
  components: {
    HelloWorld,
  },
  data: () => ({
    darkMode: false,
    learningProgress: 0,
    currentHoldIndex: 0,
    climberPose: 'catch',
    climberTop: 100 - ROUTE_HOLDS[0].bottom,
    climberLeft: ROUTE_HOLDS[0].left,
    climberLateral: 0,
    climberLift: 0,
    climberTilt: 0,
    climberScale: 1,
    climberGlow: false,
    routeHolds: ROUTE_HOLDS,

    // FX canvas & particles
    fxParticles: [],
    fxFlashes: [],
    fxRunning: false,
    fxAnimFrame: null,
    lastFxTime: 0,
    fxCtx: null,
    fxWidth: 0,
    fxHeight: 0,

    // GSAP timelines
    idleTimeline: null,
    actionTimeline: null,
  }),
  mounted() {
    const savedDarkMode = localStorage.getItem('langi_darkMode');
    if (savedDarkMode !== null) {
      this.darkMode = savedDarkMode === 'true';
    }
    this.$vuetify.theme.dark = this.darkMode;

    this.$nextTick(() => {
      this.initFxCanvas();
      this.startIdleAnimation();
    });

    window.addEventListener('resize', this.handleResize);
  },
  watch: {
    darkMode(newVal) {
      this.$vuetify.theme.dark = newVal;
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.stopAnimation();
    if (this.fxAnimFrame) {
      cancelAnimationFrame(this.fxAnimFrame);
    }
  },
  methods: {
    handleResize() {
      this.initFxCanvas();
    },
    initFxCanvas() {
      const canvas = this.$refs.fxCanvas;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      this.fxCtx = canvas.getContext('2d');
      this.fxCtx.scale(dpr, dpr);
      this.fxWidth = rect.width;
      this.fxHeight = rect.height;
    },
    getHoldCanvasCoords(hold) {
      const routeEl = this.$refs.gameRoute;
      if (!routeEl) return { x: 0, y: 0 };
      const w = routeEl.offsetWidth;
      const h = routeEl.offsetHeight;
      // FX canvas has 40px padding on all sides (inset: -40px)
      return {
        x: (hold.left / 100) * w + 40,
        y: ((100 - hold.bottom) / 100) * h + 40,
      };
    },
    startIdleAnimation() {
      this.stopIdleAnimation();
      this.idleTimeline = gsap.timeline({ repeat: -1, yoyo: true });
      this.idleTimeline
        .to(this.$data, {
          climberLift: -4,
          climberScale: 1.012,
          climberTilt: 0.8,
          duration: 2.2,
          ease: 'sine.inOut',
        })
        .to(this.$data, {
          climberLift: 3,
          climberScale: 0.99,
          climberTilt: -0.7,
          duration: 2.4,
          ease: 'sine.inOut',
        });
    },
    stopIdleAnimation() {
      if (this.idleTimeline) {
        this.idleTimeline.kill();
        this.idleTimeline = null;
      }
    },
    stopAnimation() {
      this.stopIdleAnimation();
      if (this.actionTimeline) {
        this.actionTimeline.kill();
        this.actionTimeline = null;
      }
      gsap.killTweensOf(this.$data);
      const hold = ROUTE_HOLDS[this.currentHoldIndex];
      if (hold) {
        this.climberTop = 100 - hold.bottom;
        this.climberLeft = hold.left;
      }
      this.climberLift = 0;
      this.climberLateral = 0;
      this.climberTilt = 0;
      this.climberScale = 1;
    },
    setLearningProgress(progress) {
      this.learningProgress = progress;
      const targetIndex = Math.min(
        ROUTE_HOLDS.length - 1,
        Math.round((progress / 100) * (ROUTE_HOLDS.length - 1))
      );
      if (targetIndex !== this.currentHoldIndex && !this.actionTimeline) {
        this.currentHoldIndex = targetIndex;
        const targetHold = ROUTE_HOLDS[targetIndex];
        const targetTop = 100 - targetHold.bottom;
        gsap.to(this.$data, {
          climberTop: targetTop,
          climberLeft: targetHold.left,
          climberLift: 0,
          climberLateral: 0,
          duration: 0.7,
          ease: 'power2.out',
        });
      }
    },
    applyAnswerPhysics(isCorrect) {
      this.stopAnimation();

      if (isCorrect) {
        const prevHoldIndex = this.currentHoldIndex;
        const nextHoldIndex = Math.min(ROUTE_HOLDS.length - 1, prevHoldIndex + 1);
        this.currentHoldIndex = nextHoldIndex;

        const currHold = ROUTE_HOLDS[prevHoldIndex];
        const nextHold = ROUTE_HOLDS[nextHoldIndex];
        const deltaX = nextHold.left - currHold.left;
        const tiltDirection = deltaX < 0 ? -1 : 1;
        const nextTop = 100 - nextHold.bottom;

        const timeline = gsap.timeline({
          onComplete: () => {
            this.actionTimeline = null;
            this.climberPose = 'catch';
            this.startIdleAnimation();
          },
        });
        this.actionTimeline = timeline;

        // 1. Compression / Coil phase
        timeline
          .set(this.$data, { climberPose: 'catch' })
          .to(this.$data, {
            climberLift: 15,
            climberScale: 0.94,
            climberTilt: tiltDirection * -3,
            duration: 0.16,
            ease: 'power2.in',
          })
          .call(() => {
            this.emitChalkBurst(currHold, 8, -tiltDirection);
          })
          // 2. Explosive Dyno Lunge towards target hold
          .set(this.$data, { climberPose: 'reach' })
          .to(this.$data, {
            climberTop: nextTop - 2.5,
            climberLeft: nextHold.left,
            climberLift: -35,
            climberScale: 1.06,
            climberTilt: tiltDirection * 7,
            duration: 0.42,
            ease: 'power3.out',
          })
          // 3. Sticking the Hold Catch
          .call(() => {
            this.climberPose = 'catch';
            this.emitHoldFlash(nextHold);
            this.emitChalkBurst(nextHold, 16, tiltDirection);
            this.climberGlow = true;
            setTimeout(() => { this.climberGlow = false; }, 420);
          })
          .to(this.$data, {
            climberTop: nextTop,
            climberLift: 8,
            climberScale: 0.98,
            climberTilt: tiltDirection * -9,
            duration: 0.18,
            ease: 'back.out(2)',
          })
          // 4. Pendulum Leg Swing & Momentum damping
          .to(this.$data, {
            climberLift: -2,
            climberScale: 1.01,
            climberTilt: tiltDirection * 5,
            duration: 0.28,
            ease: 'sine.inOut',
          })
          .to(this.$data, {
            climberLift: 1,
            climberTilt: tiltDirection * -2,
            duration: 0.24,
            ease: 'sine.inOut',
          })
          .to(this.$data, {
            climberLift: 0,
            climberLateral: 0,
            climberTilt: 0,
            climberScale: 1,
            duration: 0.22,
            ease: 'sine.out',
          });
      } else {
        // Slip / Wrong answer physics
        const currHold = ROUTE_HOLDS[this.currentHoldIndex];
        const slipDirection = Math.random() > 0.5 ? 1 : -1;

        const timeline = gsap.timeline({
          onComplete: () => {
            this.actionTimeline = null;
            this.climberPose = 'catch';
            this.startIdleAnimation();
          },
        });
        this.actionTimeline = timeline;

        timeline
          // 1. Slip & drop with gravity
          .set(this.$data, { climberPose: 'reach' })
          .to(this.$data, {
            climberLift: 32,
            climberLateral: slipDirection * 15,
            climberTilt: slipDirection * 13,
            climberScale: 0.96,
            duration: 0.22,
            ease: 'power3.in',
          })
          .call(() => {
            this.emitChalkDustFall(currHold);
          })
          // 2. Desperate recovery grab & bounce
          .call(() => {
            this.climberPose = 'catch';
          })
          .to(this.$data, {
            climberLift: -10,
            climberLateral: slipDirection * -4,
            climberTilt: slipDirection * -5,
            climberScale: 1.03,
            duration: 0.38,
            ease: 'elastic.out(1.2, 0.45)',
          })
          // 3. Re-stabilization
          .to(this.$data, {
            climberLift: 0,
            climberLateral: 0,
            climberTilt: 0,
            climberScale: 1,
            duration: 0.28,
            ease: 'sine.out',
          });
      }
    },
    emitChalkBurst(hold, count = 16, direction = 1) {
      const { x, y } = this.getHoldCanvasCoords(hold);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 1.5) + (Math.random() - 0.5) * 1.6;
        const speed = 35 + Math.random() * 95;
        this.fxParticles.push({
          x: x + (Math.random() - 0.5) * 12,
          y: y + (Math.random() - 0.5) * 12,
          vx: Math.cos(angle) * speed + direction * (15 + Math.random() * 20),
          vy: Math.sin(angle) * speed,
          size: 2 + Math.random() * 4,
          alpha: 0.85 + Math.random() * 0.15,
          decay: 1.1 + Math.random() * 0.8,
          gravity: 140 + Math.random() * 60,
          color: Math.random() > 0.4 ? '#FFFDF5' : '#FCE7C8',
        });
      }
      this.startFxLoop();
    },
    emitHoldFlash(hold) {
      const { x, y } = this.getHoldCanvasCoords(hold);
      this.fxFlashes.push({
        x,
        y,
        radius: 8,
        maxRadius: 42,
        alpha: 1,
        color: '#F59E0B',
        growth: 85,
      });
      this.startFxLoop();
    },
    emitChalkDustFall(hold) {
      const { x, y } = this.getHoldCanvasCoords(hold);
      for (let i = 0; i < 18; i++) {
        this.fxParticles.push({
          x: x + (Math.random() - 0.5) * 26,
          y: y + Math.random() * 10,
          vx: (Math.random() - 0.5) * 30,
          vy: 20 + Math.random() * 60,
          size: 1.8 + Math.random() * 3,
          alpha: 0.7 + Math.random() * 0.3,
          decay: 0.9 + Math.random() * 0.5,
          gravity: 120,
          color: '#FFF8EC',
        });
      }
      this.startFxLoop();
    },
    startFxLoop() {
      if (this.fxRunning) return;
      this.fxRunning = true;
      this.lastFxTime = performance.now();
      this.fxAnimFrame = requestAnimationFrame(this.tickFx);
    },
    tickFx(time) {
      const dt = Math.min(0.1, (time - this.lastFxTime) / 1000);
      this.lastFxTime = time;

      const ctx = this.fxCtx;
      if (!ctx) {
        this.fxRunning = false;
        return;
      }

      ctx.clearRect(0, 0, this.fxWidth, this.fxHeight);

      // Render & update hold flashes
      for (let i = this.fxFlashes.length - 1; i >= 0; i--) {
        const f = this.fxFlashes[i];
        f.radius += f.growth * dt;
        f.alpha -= dt * 2.2;
        if (f.alpha <= 0 || f.radius >= f.maxRadius) {
          this.fxFlashes.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.radius, 0, Math.PI * 2);
        ctx.strokeStyle = f.color;
        ctx.lineWidth = 3 * f.alpha;
        ctx.globalAlpha = f.alpha;
        ctx.shadowColor = '#F59E0B';
        ctx.shadowBlur = 14;
        ctx.stroke();
        ctx.restore();
      }

      // Render & update particles
      for (let i = this.fxParticles.length - 1; i >= 0; i--) {
        const p = this.fxParticles[i];
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += p.gravity * dt;
        p.vx *= 0.96;
        p.alpha -= p.decay * dt;
        if (p.alpha <= 0) {
          this.fxParticles.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.shadowColor = 'rgba(255,255,255,0.8)';
        ctx.shadowBlur = 5;
        ctx.fill();
        ctx.restore();
      }

      if (this.fxFlashes.length > 0 || this.fxParticles.length > 0) {
        this.fxAnimFrame = requestAnimationFrame(this.tickFx);
      } else {
        this.fxRunning = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "./scss/variables.scss";

#inspire {
  font-family: $body-font-family !important;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  background-color: #eef1ed !important;
}

#inspire::before {
  position: fixed;
  z-index: -2;
  inset: 0;
  content: '';
  background-image:
    linear-gradient(90deg, rgba(246, 248, 246, 0.95) 0%, rgba(243, 246, 242, 0.84) 44%, rgba(238, 241, 237, 0.52) 100%),
    url('./assets/climbing-gym-wall.png');
  background-position: center, 80% center;
  background-size: cover, auto 145%;
}

#inspire.is-dark {
  background-color: #0b121c !important;
}

#inspire.is-dark::before {
  background-image:
    linear-gradient(90deg, rgba(11, 18, 28, 0.96) 0%, rgba(11, 18, 28, 0.86) 46%, rgba(11, 18, 28, 0.58) 100%),
    url('./assets/climbing-gym-wall.png');
  filter: saturate(0.72) brightness(0.62);
}

.app-frame {
  position: relative;
  z-index: 0;
}

.climbing-scene {
  position: fixed;
  z-index: 1;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.game-route {
  position: absolute;
  top: 8vh;
  right: clamp(35px, 7vw, 150px);
  width: clamp(130px, 15vw, 240px);
  height: 80vh;
  pointer-events: none;
  overflow: visible;
}

.game-route::before {
  position: absolute;
  top: 2%;
  bottom: 3%;
  left: 50%;
  width: 2px;
  content: '';
  background: linear-gradient(to bottom, rgba(245, 158, 11, 0.8), rgba(245, 158, 11, 0.2));
  transform: rotate(8deg);
  pointer-events: none;
  border-radius: 99px;
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.3);
}

.route-hold {
  position: absolute;
  width: 16px;
  height: 16px;
  margin-left: -8px;
  margin-bottom: -8px;
  border: 2px solid rgba(255, 246, 229, 0.85);
  border-radius: 50%;
  background: rgba(189, 99, 58, 0.92);
  box-shadow: 0 0 0 4px rgba(189, 99, 58, 0.16), 0 4px 10px rgba(44, 28, 17, 0.35);
  transition: transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1), background 300ms ease, box-shadow 300ms ease;
  z-index: 3;
}

.route-hold.is-reached {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border-color: #fffbeb;
  box-shadow: 0 0 0 5px rgba(245, 158, 11, 0.3), 0 0 16px rgba(245, 158, 11, 0.8);
  transform: scale(1.15);
}

.route-hold.is-current {
  background: linear-gradient(135deg, #10b981, #059669);
  border-color: #ffffff;
  box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.4), 0 0 22px rgba(16, 185, 129, 0.95);
  transform: scale(1.35);
  animation: hold-current-pulse 1.8s infinite ease-in-out;
}

.route-hold.is-top {
  border-radius: 3px;
  transform: rotate(45deg);
}

.route-hold.is-top.is-reached {
  transform: rotate(45deg) scale(1.25);
}

@keyframes hold-current-pulse {
  0%, 100% {
    box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.35), 0 0 14px rgba(16, 185, 129, 0.7);
  }
  50% {
    box-shadow: 0 0 0 9px rgba(16, 185, 129, 0.5), 0 0 28px rgba(16, 185, 129, 1);
  }
}

.route-fx-canvas {
  position: absolute;
  inset: -40px;
  width: calc(100% + 80px);
  height: calc(100% + 80px);
  pointer-events: none;
  z-index: 10;
}

.route-top {
  position: absolute;
  top: 5vh;
  right: clamp(65px, 10vw, 195px);
  padding: 5px 12px;
  border: 1px solid rgba(255, 237, 204, 0.72);
  border-radius: 999px;
  color: #fff5e5;
  background: rgba(52, 65, 60, 0.85);
  box-shadow: 0 7px 18px rgba(32, 22, 14, 0.2);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  display: flex;
  align-items: center;
  transition: all 400ms ease;
  z-index: 6;
}

.route-top.is-reached {
  background: linear-gradient(135deg, #f59e0b, #d97706) !important;
  color: #ffffff !important;
  border-color: #fffbeb !important;
  box-shadow: 0 0 28px rgba(245, 158, 11, 0.9), 0 8px 20px rgba(0, 0, 0, 0.35) !important;
  transform: scale(1.15);
  animation: top-trophy-pulse 2s infinite alternate;
}

@keyframes top-trophy-pulse {
  0% { box-shadow: 0 0 18px rgba(245, 158, 11, 0.7), 0 6px 16px rgba(0,0,0,0.3); }
  100% { box-shadow: 0 0 32px rgba(245, 158, 11, 1), 0 8px 24px rgba(0,0,0,0.4); }
}

.climber-rig {
  position: absolute;
  top: var(--climber-top);
  left: var(--climber-left);
  width: clamp(170px, 18vw, 290px);
  pointer-events: none;
  z-index: 5;
  transform: translate3d(calc(-14.3% + var(--climber-x)), calc(-4.8% + var(--climber-lift)), 0) rotate(var(--climber-tilt)) scale(var(--climber-scale));
  transform-origin: 14.3% 4.8%;
  will-change: transform, top, left;
  transition: filter 180ms ease;
}

.game-climber {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 16px 22px rgba(28, 20, 12, 0.45));
  transition: opacity 120ms ease;
}

.game-climber--catch {
  opacity: 1;
}

.game-climber--reach {
  position: absolute;
  inset: 0;
  opacity: 0;
}

.climber-rig.is-reach .game-climber--catch {
  opacity: 0;
}

.climber-rig.is-reach .game-climber--reach {
  opacity: 1;
  filter: drop-shadow(0 0 22px rgba(245, 158, 11, 0.65)) drop-shadow(0 16px 22px rgba(28, 20, 12, 0.45));
}

.climber-rig.is-glowing .game-climber {
  filter: drop-shadow(0 0 26px rgba(245, 158, 11, 0.95)) drop-shadow(0 16px 22px rgba(28, 20, 12, 0.45)) !important;
}

.brand-bar {
  color: #f8f4ed;
  background:
    linear-gradient(115deg, rgba(27, 39, 39, 0.98), rgba(52, 65, 60, 0.96)),
    linear-gradient(90deg, #1b2727, #34413c);
  border-bottom: 1px solid rgba(213, 157, 110, 0.44);
}

.brand-overline {
  color: #d9ad81;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.2em;
}

.brand-mark {
  color: #d9ad81 !important;
}

.brand-name {
  color: #f8f4ed;
  font-size: 29px;
  font-weight: 750;
  letter-spacing: 0.04em;
  line-height: 1;
}

.brand-tagline {
  color: rgba(248, 244, 237, 0.72);
  font-size: 13px;
  font-weight: 500;
}

.learning-shell {
  background: rgba(255, 255, 255, 0.88) !important;
  border: 1px solid rgba(228, 233, 229, 0.85);
  box-shadow: 0 24px 70px rgba(27, 39, 39, 0.12) !important;
  backdrop-filter: blur(18px);
}

.learning-content {
  background: rgba(255, 255, 255, 0.75);
}

.is-dark .learning-shell {
  background: rgba(14, 22, 30, 0.88) !important;
  border-color: rgba(148, 163, 184, 0.16);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.45) !important;
}

.is-dark .learning-content {
  background: rgba(22, 32, 42, 0.78);
}

@media (max-width: 600px) {
  #inspire::before {
    background-position: center, 84% center;
    background-size: cover, auto 128%;
  }

  .learning-shell {
    backdrop-filter: blur(12px);
  }

  .brand-name {
    font-size: 25px;
  }

  .game-route,
  .route-top {
    display: none !important;
  }
}

.rounded-xl {
  border-radius: 20px !important;
}

.rounded-lg {
  border-radius: 14px !important;
}

/* Global dark mode menu styling */
.v-menu__content.theme--dark {
  background-color: #16222f !important;
}

.theme--dark .v-list {
  background-color: #16222f !important;
}

.theme--dark .v-list-item {
  color: #f3f6f5 !important;
}

.theme--dark .v-list-item__title {
  color: #f3f6f5 !important;
}

.theme--dark .v-list-item__subtitle {
  color: #9cb0aa !important;
}

.theme--dark .v-list-item:hover {
  background-color: #243547 !important;
}

.theme--dark .v-list-item--active {
  background-color: #243547 !important;
}
</style>
